import { type LabTestType, LabTestTypes } from "./LabTestDefinitions";

export interface FilterLeafNode {
  methodCode: string;
  standard: string;
  canCreate: boolean;
}

export interface FilterTestNode {
  id: string;
  label: string;
  typeCode: string;
  leaves: FilterLeafNode[];
}

export interface FilterGroupNode {
  id: string;
  label: string;
  tests: FilterTestNode[];
}

const byLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);

/**
 * Builds the three-level catalogue tree (Test Group → Test Name → Standard) shown by the
 * test list filter and the Add Test modal. Hidden types/methods are excluded; methods are
 * grouped into test-name nodes by `testName ?? type.name`; groups and tests are alphabetical.
 */
export function buildLabTestFilterTree(types: readonly LabTestType[] = LabTestTypes): FilterGroupNode[] {
  const groups = new Map<string, Map<string, FilterTestNode>>();

  for (const type of types) {
    if (type.hide) continue;
    for (const method of type.types) {
      if (method.hide) continue;
      const label = method.testName ?? type.name;
      const tests = groups.get(type.group) ?? new Map<string, FilterTestNode>();
      groups.set(type.group, tests);
      const test = tests.get(label) ?? { id: `test:${type.code}:${label}`, label, typeCode: type.code, leaves: [] };
      tests.set(label, test);
      test.leaves.push({ methodCode: method.code, standard: method.standard, canCreate: method.canCreate });
    }
  }

  return [...groups.entries()]
    .map(([label, tests]) => ({
      id: `group:${label}`,
      label,
      tests: [...tests.values()].sort(byLabel),
    }))
    .sort(byLabel);
}

export function collectLeafCodes(nodes: readonly (FilterGroupNode | FilterTestNode)[]): string[] {
  return nodes.flatMap((node) =>
    "tests" in node
      ? node.tests.flatMap((test) => test.leaves.map((leaf) => leaf.methodCode))
      : node.leaves.map((leaf) => leaf.methodCode),
  );
}

export interface FoundLeaf {
  group: FilterGroupNode;
  test: FilterTestNode;
  leaf: FilterLeafNode;
}

/**
 * Drops non-creatable leaves, then any Test/Group left with no leaves — used by the New Test
 * dialog's picker, which (unlike the list filter) only ever offers types the user can create.
 */
export function filterCreatableTree(tree: readonly FilterGroupNode[]): FilterGroupNode[] {
  return tree
    .map((group) => ({
      ...group,
      tests: group.tests
        .map((test) => ({ ...test, leaves: test.leaves.filter((leaf) => leaf.canCreate) }))
        .filter((test) => test.leaves.length > 0),
    }))
    .filter((group) => group.tests.length > 0);
}

export type TriState = "checked" | "unchecked" | "indeterminate";

export function getTriState(selected: ReadonlySet<string>, leafCodes: readonly string[]): TriState {
  const count = leafCodes.filter((code) => selected.has(code)).length;
  if (count === 0) return "unchecked";
  return count === leafCodes.length ? "checked" : "indeterminate";
}

/** All-on → remove all given leaves from the selection; otherwise add them all. */
export function toggleLeafCodes(selected: ReadonlySet<string>, leafCodes: readonly string[]): Set<string> {
  const next = new Set(selected);
  const allSelected = leafCodes.every((code) => next.has(code));
  for (const code of leafCodes) {
    if (allSelected) next.delete(code);
    else next.add(code);
  }
  return next;
}

/**
 * Case-insensitive substring search across group / test-name / standard labels, plus each
 * test's type code so abbreviations (e.g. "PD", "WC") find their type. A match at a level keeps
 * its whole subtree; an empty query returns the tree unchanged.
 */
export function searchFilterTree(tree: readonly FilterGroupNode[], query: string): FilterGroupNode[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...tree];

  const result: FilterGroupNode[] = [];
  for (const group of tree) {
    if (group.label.toLowerCase().includes(needle)) {
      result.push(group);
      continue;
    }
    const tests = group.tests
      .map((test) => {
        if (test.label.toLowerCase().includes(needle) || test.typeCode.toLowerCase().includes(needle)) return test;
        const leaves = test.leaves.filter((leaf) => leaf.standard.toLowerCase().includes(needle));
        return leaves.length ? { ...test, leaves } : null;
      })
      .filter((test) => test !== null);
    if (tests.length) result.push({ ...group, tests });
  }
  return result;
}

const validMethodCodes = new Set(collectLeafCodes(buildLabTestFilterTree()));

export function isValidMethodCode(code: string): boolean {
  return validMethodCodes.has(code);
}
